import { Button, Dropdown, type MenuProps } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch } from 'react-router-dom';
import ShortcutHint from '../../Common/ShortcutHint';
import { useKeyboardShortcutsContext } from '../../../contexts/KeyboardShortcutsContext';
import { InterfaceLimitationEnum } from '../../../enums/InterfaceLimitationEnum';
import { useBranding } from '../../../hooks/useBranding';
import { useFileUpload } from '../../../hooks/useFileUpload';
import type RouterPageContext from '../../../interfaces/RouterPageContext';
import { useAppStore } from '../../../store/store';
import { hasInterfaceLimitation } from '../../../utils/utils';
import { Icon } from '../Icon';

interface Props {
  context: RouterPageContext;
}

export const MainButtonMenu: React.FC<Props> = ({ context }) => {
  // Hooks for router and translations
  const location = useLocation();
  const { t } = useTranslation();

  // Custom branding and upload hooks
  const { brandingData } = useBranding();
  const { uploadFiles } = useFileUpload();

  // Selectors from the application store for file and secret creation triggers
  const triggerCreateFolder = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFolder,
  );
  const triggerCreateFileFromTemplate = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFileFromTemplate,
  );
  const triggerCreateSecretFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateSecretFile,
  );
  const triggerCreateNoteFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateNoteFile,
  );
  const isUploadDisabledInCurrentFolder = useAppStore((s) => s.isUploadDisabledInCurrentFolder);
  const openCreateMenuToken = useAppStore(
    (state) => state.appEvents.keyboardShortcuts.openCreateMenuToken,
  );

  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const lastOpenCreateMenuTokenRef = useRef(openCreateMenuToken);

  // Selector for opening the email compose dialog
  const openEmailComposer = useAppStore((s) => s.openEmailComposer);

  const { registerHandler, unregisterHandler } = useKeyboardShortcutsContext();

  // Refs for hidden file and folder input elements
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Upload shortcuts must click inputs synchronously on keydown (browser blocks async .click()).
  useEffect(() => {
    const openUploadFilePicker = () => {
      fileInputRef.current?.click();
    };
    const openUploadFolderPicker = () => {
      folderInputRef.current?.click();
    };

    registerHandler('create.uploadFile', openUploadFilePicker);
    registerHandler('create.uploadFolder', openUploadFolderPicker);

    return () => {
      unregisterHandler('create.uploadFile', openUploadFilePicker);
      unregisterHandler('create.uploadFolder', openUploadFolderPicker);
    };
  }, [registerHandler, unregisterHandler]);

  // Route matchers for conditional button rendering
  const matchFiles = useMatch('/files/*');
  const matchEmail = useMatch('/emails/*');

  /**
   * Menu items for the main button dropdown menu, memoized for performance.
   */
  const dropdownItems: MenuProps['items'] = useMemo(() => {
    return [
      // Create new folder
      {
        extra: <ShortcutHint shortcutId="create.folder" />,
        label: t('common.createFolder'),
        icon: <Icon name="FolderFilledIcon" className="text-yellow h-5 w-5" />,
        key: 'new-folder',
        onClick: () => {
          triggerCreateFolder();
        },
      },
      { type: 'divider' },
      {
        extra: <ShortcutHint shortcutId="create.uploadFile" />,
        label: t('common.uploadFile'),
        icon: <Icon name="FileUpIcon" className="h-5 w-5" />,
        key: 'upload-file',
        onClick: () => {
          fileInputRef.current?.click();
        },
      },
      {
        extra: <ShortcutHint shortcutId="create.uploadFolder" />,
        label: t('common.uploadFolder'),
        icon: <Icon name="FolderUpIcon" className="h-5 w-5" />,
        key: 'upload-folder',
        onClick: () => {
          folderInputRef.current?.click();
        },
      },
      { type: 'divider' },
      {
        extra: <ShortcutHint shortcutId="create.word" />,
        label: t('common.wordDocument'),
        icon: <Icon name="WordFileIcon" className="h-5 w-5" />,
        key: 'create-word',
        onClick: () => {
          triggerCreateFileFromTemplate('word');
        },
      },
      {
        extra: <ShortcutHint shortcutId="create.excel" />,
        label: t('common.excelDocument'),
        icon: <Icon name="ExcelFileIcon" className="h-5 w-5" />,
        key: 'create-excel',
        onClick: () => {
          triggerCreateFileFromTemplate('excel');
        },
      },
      {
        extra: <ShortcutHint shortcutId="create.powerpoint" />,
        label: t('common.pptDocument'),
        icon: <Icon name="PptFileIcon" className="h-5 w-5" />,
        key: 'create-powerpoint',
        onClick: () => {
          triggerCreateFileFromTemplate('powerpoint');
        },
      },
      { type: 'divider' },
      {
        extra: <ShortcutHint shortcutId="create.note" />,
        label: t('note.createNote'),
        icon: <Icon name="FilePenLineIcon" className="text-azure h-5 w-5" />,
        key: 'create-note',
        onClick: () => {
          triggerCreateNoteFile();
        },
      },
      {
        label: t('secret.createSecret'),
        icon: <Icon name="FileKeyIcon" className="text-yellow-dark h-5 w-5" />,
        key: 'create-secret',
        children: [
          {
            extra: <ShortcutHint shortcutId="create.secret" />,
            label: t('secret.presets.credentials'),
            icon: <Icon name="KeyRoundIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-credentials',
            onClick: () => {
              triggerCreateSecretFile('credentials');
            },
          },
          {
            extra: <ShortcutHint shortcutId="create.secretCard" />,
            label: t('secret.presets.card'),
            icon: <Icon name="CreditCardIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-card',
            onClick: () => {
              triggerCreateSecretFile('card');
            },
          },
          {
            extra: <ShortcutHint shortcutId="create.secretIdentity" />,
            label: t('secret.presets.identity'),
            icon: <Icon name="FileUserIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-identity',
            onClick: () => {
              triggerCreateSecretFile('identity');
            },
          },
          {
            extra: <ShortcutHint shortcutId="create.secretSshKey" />,
            label: t('secret.presets.ssh_key'),
            icon: <Icon name="KeyIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-ssh',
            onClick: () => {
              triggerCreateSecretFile('ssh_key');
            },
          },
          {
            extra: <ShortcutHint shortcutId="create.secretCustom" />,
            label: t('secret.presets.custom'),
            icon: <Icon name="FileSlidersIcon" className="text-yellow-dark h-5 w-5" />,
            key: 'create-secret-custom',
            onClick: () => {
              triggerCreateSecretFile('custom');
            },
          },
        ],
      },
    ];
  }, [
    t,
    triggerCreateFileFromTemplate,
    triggerCreateFolder,
    triggerCreateNoteFile,
    triggerCreateSecretFile,
  ]);

  useEffect(() => {
    if (openCreateMenuToken !== lastOpenCreateMenuTokenRef.current) {
      lastOpenCreateMenuTokenRef.current = openCreateMenuToken;
      setIsCreateMenuOpen(true);
    }
  }, [openCreateMenuToken]);

  // ponytail: upload tokens kept for non-keyboard callers; keyboard path uses registerHandler above.

  /**
   * Handles file input changes and uploads files.
   * Resets the input after handling.
   */
  const handleInputFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  /**
   * Handles folder input changes and uploads selected folder's files.
   * Resets the input after handling.
   */
  const handleInputFolderChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    uploadFiles(files as FileList);
    event.target.value = '';
  };

  // Compute button visibility according to user permissions, settings, and current route
  const showFilesButton = !isUploadDisabledInCurrentFolder && matchFiles;
  const showEmailButton =
    import.meta.env.VITE_APP_EMAIL_ENABLED === 'true' &&
    matchEmail &&
    !hasInterfaceLimitation(
      context.authenticatedUser?.settings || [],
      InterfaceLimitationEnum.DenyCreateEmail,
    );
  const showUsersButton =
    location.pathname === '/users' &&
    !hasInterfaceLimitation(
      context.authenticatedUser?.settings || [],
      InterfaceLimitationEnum.DenyShowCreateNewUser,
    );
  const showGroupsButton =
    location.pathname === '/groups' &&
    !hasInterfaceLimitation(
      context.authenticatedUser?.settings || [],
      InterfaceLimitationEnum.DenyShowCreateNewGroup,
    );

  // When no main action is available, show a disabled button instead of hiding the component
  const hasButton = showFilesButton || showEmailButton || showUsersButton || showGroupsButton;

  return (
    <div className="relative z-10 mx-auto mb-2 h-10 px-2 lg:w-[90%]">
      {/* Hidden file and folder inputs for handling upload events */}
      {showFilesButton && (
        <>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={handleInputFileChange}
          />
          <input
            type="file"
            multiple
            webkitdirectory="true"
            mozdirectory="true"
            ref={folderInputRef}
            className="hidden"
            onChange={handleInputFolderChange}
          />
        </>
      )}

      {/* Dropdown button for file operations (new, upload, template, secrets) */}
      {showFilesButton ? (
        <Dropdown
          open={isCreateMenuOpen}
          onOpenChange={setIsCreateMenuOpen}
          menu={{
            items: dropdownItems,
          }}
          className="w-full"
          trigger={['click']}
          styles={{
            root: { zIndex: 1000 },
          }}
        >
          <Button
            type="primary"
            shape="round"
            className="w-full px-2 lg:ml-0 lg:px-4"
            aria-label={t('common.new')}
            style={{
              backgroundColor: brandingData?.mainButtonsColorBackground,
              color: brandingData?.mainButtonsColorForeground,
            }}
          >
            <div className="flex w-full flex-row items-center justify-center gap-1">
              <Icon
                name="FilePlusIcon"
                className="h-5 w-5"
                style={{
                  color: brandingData?.mainButtonsColorForeground,
                }}
              />
              <span
                className="hidden font-medium lg:block"
                style={{
                  color: brandingData?.mainButtonsColorForeground,
                }}
              >
                {t('common.new')}
              </span>
            </div>
          </Button>
        </Dropdown>
      ) : null}

      {/* Email compose button, only shown if email is enabled and allowed */}
      {showEmailButton ? (
        <Button
          type="primary"
          shape="round"
          className="w-full px-2 lg:ml-0 lg:px-4"
          aria-label={t('common.new')}
          style={{
            backgroundColor: brandingData?.mainButtonsColorBackground,
            color: brandingData?.mainButtonsColorForeground,
          }}
          onClick={() => openEmailComposer()}
        >
          <div className="flex w-full flex-row items-center justify-center gap-1">
            <Icon
              name="MailPlusIcon"
              className="h-5 w-5"
              style={{
                color: brandingData?.mainButtonsColorForeground,
              }}
            />
            <span
              className="hidden font-medium lg:block"
              style={{
                color: brandingData?.mainButtonsColorForeground,
              }}
            >
              {t('common.new')}
            </span>
          </div>
        </Button>
      ) : null}

      {/* New user creation button (Users page only) */}
      {showUsersButton ? (
        <Button
          type="primary"
          shape="round"
          className="w-full!"
          aria-label={t('common.newUser')}
          onClick={() => {
            useAppStore.getState().events.emit.createUser();
          }}
        >
          <div className="flex flex-row items-center justify-center gap-1">
            <Icon name="UserPlusIcon" className="h-6" />
            <span className="hidden font-medium lg:block">{t('common.newUser')}</span>
          </div>
        </Button>
      ) : null}

      {/* New group creation button (Groups page only) */}
      {showGroupsButton ? (
        <Button
          type="primary"
          shape="round"
          className="w-full!"
          aria-label={t('common.newGroup')}
          onClick={() => {
            useAppStore.getState().events.emit.createGroup();
          }}
        >
          <div className="flex flex-row items-center justify-center gap-1">
            <Icon name="UsersPlusIcon" className="h-6" />
            <span className="hidden font-medium lg:block">{t('common.newGroup')}</span>
          </div>
        </Button>
      ) : null}

      {/* Disabled placeholder when no main action is available for the current context */}
      {!hasButton && (
        <Button
          type="primary"
          shape="round"
          className="w-full! px-2! opacity-60! lg:ml-0! lg:px-4!"
          disabled
          style={{
            backgroundColor: brandingData?.mainButtonsColorBackground,
            color: brandingData?.mainButtonsColorForeground,
          }}
        >
          <div className="flex w-full flex-row items-center justify-center gap-1">
            <Icon
              name="FilePlusIcon"
              className="h-5 w-5"
              style={{
                color: brandingData?.mainButtonsColorForeground,
              }}
            />
            <span
              className="hidden font-medium lg:block"
              style={{
                color: brandingData?.mainButtonsColorForeground,
              }}
            >
              {t('common.new')}
            </span>
          </div>
        </Button>
      )}
    </div>
  );
};

export default MainButtonMenu;
